/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/naming-convention */
import styled from 'styled-components';
import React from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import parseISO from 'date-fns/parseISO';
import { calendar } from '~/hooks/useTimeAgo';
import { formatAmount, xtz } from '~/utils';
import { media } from '~/styles/media';
import TokenAsset from './TokenAsset';
import { Objkt } from '~/types';
import LikeButton from './LikeButton';
import TextTruncated from './TextTruncated';
import useCurrentUserHoldings, { getUserHoldings } from '~/hooks/useCurrentUserHoldings';
import FlexRow from './FlexRow';
import TokenCreatorData from './TokenCreatorData';
import { ObjktPerformance } from '~/pageComponents/ObjktPage';
import { Layout, useDataContext } from '~/contexts/Data';
import useVolumeTemporality from '~/hooks/useVolumeTemporality';
import { colors } from '~/styles/themes';
// import { numberOfColumns } from './TokenGrid';

const BuyButton = dynamic(() => import('~/components/BuyButton'), {
  ssr: false,
});

const TokenContainer = styled.div<{ $layout: Layout }>`
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  @media (hover: hover) {
    &:hover {
      text-decoration: none;
    }
  }
`;

const TokenText = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 5px 0;
  ${media.desktop`
    padding: 5px 15px;
  `};
`;

const OwnText = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: bold;
`;

const ImageTag = styled.div`
  position: absolute;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: black;
  color: white;
`;

const OwnerCount = styled(ImageTag)`
  top: 10px;
  right: 10px;
`;

const StolenBanner = styled(OwnerCount)`
  background-color: ${({ theme }) => theme.colors.alert};
  left: 10px;
  right: auto;
  width: max-content;
  padding: 0 10px;
  font-weight: bold;
`;

const StyledLikeButton = styled(LikeButton)`
  margin-left: 10px;
  ${media.tablet` 
    margin-right: -10px;  
  `}
`;

const DataRowContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 5px;
`;

const DataRowCell = styled.div`
  text-align: right;
  &:nth-child(odd) {
    text-align: left;
  }
`;

const DataRowCellLabel = styled.div`
  color: ${({ theme }) => theme.colors.textLight};
  text-transform: uppercase;
`;

const DataRowCellValue = styled.div`

`;

export const DataRow = ({ values = [] }) => (
  <DataRowContainer>
    {
      values.map(({
        label,
        value,
      }) => (
        <DataRowCell key={ `cell.${label}.${value}` }>
          <DataRowCellLabel>{ label }</DataRowCellLabel>
          <DataRowCellValue>{ value }</DataRowCellValue>
        </DataRowCell>
      ))
    }
  </DataRowContainer>
);

const Token: React.FC<{
  walletOf?: string;
  objkt: Objkt;
  layout: Layout;
}> = ({
  walletOf,
  objkt,
}) => {
  const {
    id,
    title,
    // editions = 0,
    supply,
    isForSale,
    acquiredAt,
    isFromBlockedWallet,
    // isSecondarySale,
    forSaleCount,
    firstPrice,
    lastPrice,
    averageCollectPrice,
    volume,
  } = objkt || {};
  const { layout } = useDataContext();
  const acquiredAgo = acquiredAt && calendar(parseISO(acquiredAt));
  const holdings = useCurrentUserHoldings(objkt);
  const walletOfHoldings = getUserHoldings(objkt, walletOf);
  const temporality = useVolumeTemporality();
  const link = `/o/${id}`;
  return (
    <TokenContainer $layout={ layout }>
      <Link href={ link } passHref>
        <a>
          <TokenAsset { ...objkt } />
          {
            walletOfHoldings?.total > 1 ? (
              <OwnerCount>{ `x${walletOfHoldings.total}` }</OwnerCount>
            ) : null
          }
          {
            isFromBlockedWallet ? (
              <StolenBanner>BANNED ARTIST</StolenBanner>
            ) : null
          }
        </a>
      </Link>
      {
        layout === 'gallery' ? null : (
          <>
            <TokenText>
              <div style={ { flex: 1 } }>
                <FlexRow style={ { marginBottom: 5 } }>
                  <div style={ { flex: 1 } }>
                    <TokenCreatorData objkt={ objkt } />
                  </div>
                  <StyledLikeButton id={ +id } />
                </FlexRow>
                <div style={ { display: 'grid' } }>
                  <Link href={ `/o/${id}` } passHref>
                    <TextTruncated as="a" style={ { flex: 1, fontWeight: 'bold' } }>
                      {title}
                    </TextTruncated>
                  </Link>
                </div>
                {/*
                <DataRow values={
                  [
                    {
                      label: 'Editions',
                      value: formatAmount(supply || 0),
                    },
                    {
                      label: 'For sale',
                      value: formatAmount(forSaleCount),
                    },
                    firstTrade && {
                      label: 'Mint price',
                      value: `${formatAmount(firstTrade.swap.price / DIVIDER, 2)} ${xtz}`,
                    },
                    lastTrade && {
                      label: 'Last price',
                      value: `${formatAmount(lastTrade.swap.price / DIVIDER, 2)} ${xtz}`,
                    },
                  ].filter(Boolean)
                }
                />
                */}
                <div>
                  {`${formatAmount(supply || 0)} edition${supply > 1 ? 's' : ''}`}
                  {
                    forSaleCount > 0 ? (
                      ` / ${formatAmount(forSaleCount)} for sale`
                    ) : null
                  }
                </div>
                {
                  layout === 'advanced' ? (
                    <>
                      {
                        firstPrice ? (
                          <div>{ `Originally sold for ${formatAmount(firstPrice, 2)} ${xtz}`}</div>
                        ) : null
                      }
                      {
                        lastPrice && supply > 1 ? (
                          <div>
                            <span>{`Last sold for ${formatAmount(lastPrice, 2)} ${xtz}`}</span>
                            <ObjktPerformance firstPrice={ firstPrice } lastPrice={ lastPrice } />
                          </div>
                        ) : null
                      }
                      {
                        averageCollectPrice ? (
                          <div>
                            <span>{`Collected for ${formatAmount(averageCollectPrice, 2)} ${xtz}`}</span>
                          </div>
                        ) : null
                      }
                    </>
                  ) : null
                }

                {
                  holdings.total > 0 && !walletOf ? (
                    <OwnText>
                      {`You own ${holdings.total} edition${holdings.total > 1 ? 's' : ''}${holdings.listings > 0 ? ` (${holdings.listings} listed for sale)` : ''}`}
                    </OwnText>
                  ) : null
                }
                {
                  acquiredAgo ? (
                    <div>{`Received ${acquiredAgo}`}</div>
                  ) : null
                }
                {
                  volume && temporality ? (
                    <div style={ { color: colors.alert, fontWeight: 'bold' } }>{`${formatAmount(volume, 0)} ${xtz} sales volume in the last ${temporality}`}</div>
                  ) : null
                }

              </div>
            </TokenText>
            {
              isForSale ? (
                <div style={ { marginTop: 2 } }>
                  <BuyButton { ...objkt } style={ { width: '100%' } } />
                </div>
              ) : null
            }
          </>
        )
      }
    </TokenContainer>
  );
};

export default Token;
